import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import dashboardData from "../data/private/dashboard.json";
import authCopy from "../data/private/auth.json";
import { useAuth } from "../context/AuthContext";
import { isFeatureEnabled, usePlatformContext } from "../context/PlatformContext";
import { isAdminModuleEnabled } from "../config/features";
import { logActivity } from "../utils/activity";
import CommandPalette from "./CommandPalette";
import { isUiFeatureVisible } from "../utils/featureControl";
import DashboardBreadcrumbs from "./dashboard/DashboardBreadcrumbs";
import DebugSnapshotPanel from "./debug/DebugSnapshotPanel";

const readSelection = () => ({
  client: localStorage.getItem("myv_selected_client") || "",
  property: localStorage.getItem("myv_selected_property") || "",
});

export default function DashboardLayout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selection, setSelection] = useState(() => readSelection());
  const selectedClientId = localStorage.getItem("myv_selected_client_id") || "";
  const selectedPropertyId =
    localStorage.getItem("myv_selected_property_id") || "";
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { user, features } = usePlatformContext();
  const debugEnabled =
    new URLSearchParams(location.search).get("debug") === "true" ||
    localStorage.getItem("myv_debug") === "true";

  const featureKeyByPath = useMemo(
    () => ({
      "/dashboard": { platform: "dashboard", ui: "dashboard" },
      "/dashboard/clients": { platform: "clients", ui: "clients" },
      "/dashboard/properties": { platform: "properties", ui: "properties" },
      "/dashboard/analysis": { platform: "analysis", ui: "analysis" },
      "/dashboard/reports": { platform: "reports", ui: "reports" },
      "/dashboard/directions": { platform: "vastu.enabled", ui: "vastu.enabled" },
      "/dashboard/remedies": { platform: null, ui: "remedies" },
      "/dashboard/prescriptions": { platform: null, ui: "prescriptions" },
      "/dashboard/learn": { platform: null, ui: "learn" },
      "/dashboard/help": { platform: null, ui: "help" },
      "/dashboard/saved-analyses": { platform: null, ui: "saved-analyses" },
      "/dashboard/settings": { platform: "settings", ui: "settings" },
      "/dashboard/insights": { platform: null, ui: "insights" },
      "/dashboard/recommendations": { platform: null, ui: "recommendations" },
      "/dashboard/billing": { platform: "billing", ui: "billing" },
      "/dashboard/workspace": { platform: "workspaceLayouts", ui: "workspaceLayouts" },
      "/dashboard/layout": { platform: "workspaceLayouts", ui: "workspaceLayouts" },
      "/dashboard/devta": { platform: "vastu.tools", ui: "vastu.tools" },
      "/dashboard/ayadi": { platform: "vastu.tools", ui: "vastu.tools" },
      "/dashboard/numerology": { platform: "vastu.tools", ui: "vastu.tools" },
      "/dashboard/astro-vastu": { platform: "vastu.tools", ui: "vastu.tools" },
      "/dashboard/lal-kitab": { platform: "vastu.tools", ui: "vastu.tools" },
    }),
    []
  );

  const navItems = useMemo(
    () =>
      dashboardData.sidebar.items.filter((item) => {
        const keys = featureKeyByPath[item.path];
        if (!keys) return true;
        if (keys.ui && !isUiFeatureVisible(keys.ui)) return false;
        if (keys.platform && !isFeatureEnabled(features, keys.platform)) return false;
        return true;
      }),
    [featureKeyByPath, features]
  );

  const handleLogout = () => {
    logout();
    logActivity("auth_logout", { route: location.pathname });
    navigate("/login");
  };

  React.useEffect(() => {
    const handleSelectionUpdate = () => setSelection(readSelection());
    handleSelectionUpdate();
    window.addEventListener("myv_selection_updated", handleSelectionUpdate);
    return () =>
      window.removeEventListener("myv_selection_updated", handleSelectionUpdate);
  }, []);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <CommandPalette />
      <aside
        className={`hidden border-r border-slate-200 bg-white transition-all duration-300 md:flex md:flex-col ${
          collapsed ? "w-20" : "w-64"
        }`}
      >
        <div className="flex items-center justify-between px-4 py-4">
          <p className="text-sm font-semibold text-slate-900">
            {dashboardData.sidebar.title}
          </p>
          <button
            className="btn btn-secondary px-3 py-1.5 text-xs"
            onClick={() => setCollapsed((prev) => !prev)}
          >
            {dashboardData.sidebar.collapseLabel}
          </button>
        </div>
        <nav className="flex-1 space-y-1 px-3 pb-4">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition ${
                  isActive
                    ? "bg-amber-50 text-amber-700"
                    : "text-slate-600 hover:bg-slate-100"
                }`
              }
              onClick={() =>
                logActivity("nav_click", { label: item.label, to: item.path })
              }
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-700">
                {item.label.slice(0, 2).toUpperCase()}
              </span>
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-200 p-4">
          <button className="btn btn-secondary w-full" onClick={handleLogout}>
            {dashboardData.sidebar.logoutLabel}
          </button>
        </div>
      </aside>

      <div className="flex min-h-screen flex-1 flex-col">
        <div className="sticky top-0 z-10 border-b border-slate-200 bg-amber-50 px-4 py-2 text-xs text-slate-700 md:px-6">
          <span className="font-semibold text-slate-900">
            {dashboardData.contextBanner.label}
          </span>{" "}
          {selection.client || selection.property
            ? `${selection.client} ${
                selection.property ? `→ ${selection.property}` : ""
              }`
            : selectedClientId
            ? "Select a property to continue."
            : "Select a client to continue."}
        </div>
        <header className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-4 md:px-6">
          <div className="flex items-center gap-2">
            <div>
              <p className="text-xs text-slate-500">
                {dashboardData.topbar.greetingPrefix},{" "}
                {user?.name || user?.role}
              </p>
              <p className="text-xl font-semibold text-slate-900">
                {dashboardData.sidebar.title}
              </p>
            </div>
            <span
              className="inline-flex h-2 w-2 rounded-full bg-emerald-500"
              title={authCopy.signup.fields.consentLabel}
            />
          </div>
          <div className="flex items-center gap-2">
            {isAdminModuleEnabled(user) && isUiFeatureVisible("analytics") && (
              <NavLink className="btn btn-secondary" to="/admin/overview">
                Admin
              </NavLink>
            )}
            <button
              className="btn btn-secondary md:hidden"
              onClick={() => setMobileOpen(true)}
            >
              {dashboardData.sidebar.collapseLabel}
            </button>
            <button className="btn btn-secondary">
              {dashboardData.topbar.notificationsLabel}
            </button>
          </div>
        </header>

        <main className="flex-1 space-y-6 px-4 py-6 md:px-8">
          <div className="sticky top-0 z-10 rounded-lg bg-slate-50/95 py-2 backdrop-blur md:static md:bg-transparent md:py-0">
            <DashboardBreadcrumbs />
          </div>
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            {children}
          </motion.div>
        </main>
      </div>

      <DebugSnapshotPanel enabled={debugEnabled} />

      {mobileOpen && (
        <div className="fixed inset-0 z-40 flex md:hidden">
          <motion.div
            className="flex h-full w-72 flex-col border-r border-slate-200 bg-white"
            initial={{ x: -40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-center justify-between px-4 py-4">
              <p className="text-sm font-semibold text-slate-900">
                {dashboardData.sidebar.title}
              </p>
              <button
                className="btn btn-secondary px-3 py-1.5 text-xs"
                onClick={() => setMobileOpen(false)}
              >
                {dashboardData.sidebar.collapseLabel}
              </button>
            </div>
            <nav className="flex-1 space-y-1 px-3 pb-4">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition ${
                      isActive
                        ? "bg-amber-50 text-amber-700"
                        : "text-slate-600 hover:bg-slate-100"
                    }`
                  }
                  onClick={() => {
                    logActivity("nav_click", {
                      label: item.label,
                      to: item.path,
                    });
                    setMobileOpen(false);
                  }}
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-700">
                    {item.label.slice(0, 2).toUpperCase()}
                  </span>
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </nav>
            <div className="border-t border-slate-200 p-4">
              <button className="btn btn-secondary w-full" onClick={handleLogout}>
                {dashboardData.sidebar.logoutLabel}
              </button>
            </div>
          </motion.div>
          <button
            className="flex-1 bg-slate-900/40"
            onClick={() => setMobileOpen(false)}
            aria-label={dashboardData.sidebar.collapseLabel}
          />
        </div>
      )}
    </div>
  );
}
